import { H6 } from "@sellar-io/components";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { shallow } from "zustand/shallow";
import { Icon } from "../../../../components";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { BasicDialog } from "../../../../components/modal/BasicDialog";

import { DiscoveryFilterButton } from "../components/DiscoveryFilterButton";
import { FilterDrawerHeader } from "../components/DiscoveryMobileFilterDrawer";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { CollapsableFilterCard } from "./FilterCard";
import {
  draughtPriceFilterType,
  packagedPriceFilterType,
  PriceType,
} from "./FilterPriceTypes";
import {
  getPriceTypeCategory,
  getPriceTypeCategoryFromPrice,
} from "./FilterPriceCard.shared";

export type PriceTypeCategory = "packaged" | "draught";

export const FilterPriceCard = ({
  setPriceTypeCategory,
  priceTypeCategory,
  onClick,
}: {
  priceTypeCategory: PriceTypeCategory | undefined;
  setPriceTypeCategory: (
    priceTypeCategory: PriceTypeCategory | undefined,
  ) => void;
  onClick?: () => void;
}) => {
  const isDesktop = useMediaQuery("(min-width: 1084px)");
  const { selectedPriceTypes, selectedContainerTypes } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        selectedPriceTypes: state.selectedPriceTypes,
        addPriceType: state.addPriceType,
        removePriceType: state.removePriceType,
        setPriceTypes: state.setPriceTypes,
        selectedContainerTypes: state.selectedContainerTypes,
      })),
    );

  useEffect(() => {
    if (!priceTypeCategory) {
      setPriceTypeCategory(
        getPriceTypeCategory(selectedContainerTypes) ?? undefined,
      );
    }
  }, [selectedContainerTypes, priceTypeCategory, setPriceTypeCategory]);

  useEffect(() => {
    const unsubscribe = useDiscoveryV2SearchStore.subscribe(
      (state) => ({
        selectedContainerTypes: state.selectedContainerTypes,
        selectedPriceTypes: state.selectedPriceTypes,
      }),
      (state, prev) => {
        if (
          state.selectedContainerTypes.length === 0 &&
          state.selectedPriceTypes.length === 0
        ) {
          setPriceTypeCategory(undefined);
        } else if (
          priceTypeCategory === undefined &&
          prev.selectedContainerTypes.length === 0 &&
          state.selectedPriceTypes.length === 0
        ) {
          setPriceTypeCategory(
            getPriceTypeCategory(state.selectedContainerTypes),
          );
        }
      },
      { equalityFn: shallow },
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [priceTypeCategory, setPriceTypeCategory]);

  useEffect(() => {
    // Pre opens either packaged or draught price menu if coming from the market homepage after selecting a price
    if (
      selectedPriceTypes.length === 1 &&
      selectedPriceTypes[0] &&
      selectedContainerTypes.length === 0 &&
      priceTypeCategory === undefined
    ) {
      setPriceTypeCategory(
        getPriceTypeCategoryFromPrice(selectedPriceTypes[0]),
      );
    }
  }, [
    setPriceTypeCategory,
    priceTypeCategory,
    selectedContainerTypes.length,
    selectedPriceTypes,
    selectedPriceTypes.length,
  ]);

  const priceFilters =
    priceTypeCategory === "packaged"
      ? packagedPriceFilterType
      : draughtPriceFilterType;

  return (
    <>
      {isDesktop ? (
        <FilterPriceCardDesktop
          setPriceTypeCategory={setPriceTypeCategory}
          priceTypeCategory={priceTypeCategory}
          priceFilters={priceFilters}
        />
      ) : (
        <FilterPriceCardMobile
          setPriceTypeCategory={setPriceTypeCategory}
          priceTypeCategory={priceTypeCategory}
          priceFilters={priceFilters}
          onClick={onClick}
        />
      )}
    </>
  );
};

const FilterPriceCardMobile = ({
  setPriceTypeCategory,
  priceTypeCategory,
  priceFilters,
  onClick,
}: {
  priceTypeCategory: PriceTypeCategory | undefined;
  setPriceTypeCategory: (
    priceTypeCategory: PriceTypeCategory | undefined,
  ) => void;
  priceFilters: typeof packagedPriceFilterType | typeof draughtPriceFilterType;
  onClick?: () => void;
}) => {
  const { selectedPriceTypes, removePriceType, addPriceType, setPriceTypes } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        selectedPriceTypes: state.selectedPriceTypes,
        addPriceType: state.addPriceType,
        removePriceType: state.removePriceType,
        setPriceTypes: state.setPriceTypes,
      })),
    );
  return (
    <div className={"flex flex-col"}>
      <FilterDrawerHeader title={"Price"} onClick={onClick} />
      <div className={"mt-4 p-4"}>
        {priceTypeCategory ? (
          <div className={"flex flex-col gap-1"}>
            {Object.keys(priceFilters).map((priceTypeKey) => (
              <DiscoveryFilterButton
                key={priceTypeKey}
                selected={selectedPriceTypes.includes(priceTypeKey)}
                onClick={() => {
                  if (selectedPriceTypes.includes(priceTypeKey)) {
                    return removePriceType(priceTypeKey as PriceType);
                  }
                  addPriceType(priceTypeKey as PriceType);
                }}
                title={priceFilters[priceTypeKey as PriceType].name}
              />
            ))}
          </div>
        ) : (
          <>
            <InputContainer>
              <InputSelect
                onChange={(e) => {
                  if (e.target.value === "all") {
                    setPriceTypes([]);
                    return setPriceTypeCategory(undefined);
                  }
                  setPriceTypes([]);
                  setPriceTypeCategory(e.target.value as PriceTypeCategory);
                }}
              >
                <InputOption selected={!selectedPriceTypes} value={"all"}>
                  All
                </InputOption>
                <InputOption
                  value={"packaged"}
                  selected={selectedPriceTypes.includes("packaged")}
                >
                  Packaged items
                </InputOption>
                <InputOption
                  value={"draught"}
                  selected={selectedPriceTypes.includes("draught")}
                >
                  Draught items
                </InputOption>
              </InputSelect>
            </InputContainer>
          </>
        )}
      </div>
    </div>
  );
};

const FilterPriceCardDesktop = ({
  setPriceTypeCategory,
  priceTypeCategory,
  priceFilters,
}: {
  priceTypeCategory: PriceTypeCategory | undefined;
  setPriceTypeCategory: (
    priceTypeCategory: PriceTypeCategory | undefined,
  ) => void;
  priceFilters: typeof packagedPriceFilterType | typeof draughtPriceFilterType;
}) => {
  const [dialogActive, setDialogActive] = useState(false);
  const { selectedPriceTypes, removePriceType, addPriceType, setPriceTypes } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        selectedPriceTypes: state.selectedPriceTypes,
        addPriceType: state.addPriceType,
        removePriceType: state.removePriceType,
        setPriceTypes: state.setPriceTypes,
      })),
    );
  return (
    <CollapsableFilterCard
      header={
        <div className={"flex items-center"}>
          <H6
            className={twMerge("cursor-pointer")}
            onClick={() => {
              setPriceTypeCategory(undefined);
              setPriceTypes([]);
            }}
          >
            Price range
          </H6>
          {priceTypeCategory ? (
            <>
              <Icon icon={"CHEVRON_RIGHT"} />
              {priceTypeCategory === "packaged"
                ? "Packaged items"
                : "Draught items"}
            </>
          ) : null}
        </div>
      }
    >
      {priceTypeCategory ? (
        Object.keys(priceFilters).map((priceTypeKey) => (
          <DiscoveryFilterButton
            key={priceTypeKey}
            title={priceFilters[priceTypeKey as PriceType].name}
            selected={selectedPriceTypes.includes(priceTypeKey)}
            onClick={() => {
              if (selectedPriceTypes.includes(priceTypeKey)) {
                return removePriceType(priceTypeKey as PriceType);
              }
              addPriceType(priceTypeKey as PriceType);
            }}
          />
        ))
      ) : (
        <>
          <DiscoveryFilterButton
            title={"Packaged items"}
            onClick={() => setPriceTypeCategory("packaged")}
          />

          <DiscoveryFilterButton
            title={"Draught items"}
            onClick={() => setPriceTypeCategory("draught")}
          />
        </>
      )}
      <div
        className={"flex cursor-pointer items-center gap-1 text-xs"}
        onClick={() => setDialogActive(true)}
      >
        <Icon icon={"INFORMATION_OUTLINE"} size={0.6} /> Your pricing may differ
        to the list price shown
      </div>
      <BasicDialog
        dialogActive={dialogActive}
        onDismiss={() => setDialogActive(false)}
        title={"Prices on the market"}
      >
        <p className={"font-bold"}>
          The pricing for products you see on the Market is the List price for
          those products. This may be different to your pricing due to factors
          such as your customer discounts, delivery rates and any applicable
          volume discounts.
        </p>
        <br />
        <p>
          You’ll be able to see the correct pricing with any discounts applied
          when you view a product, if it’s producer has added you as a customer
          on Sellar. If you’re not added as a customer of theirs on Sellar yet,
          or not seeing the right prices, drop them a quick message to request
          your pricing.
        </p>
      </BasicDialog>
    </CollapsableFilterCard>
  );
};
